import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Glyph, type GlyphMeaning } from "@apf/design-system/glyphs";
import type { FrameworkSnapshot } from "@apf/framework-config";
import {
  SEARCH_GROUP,
  buildSearchIndex,
  querySearchIndex,
  scoreEntry,
  type SearchDocument,
  type SearchIndexArtifact,
} from "@apf/framework-config/search-index";
import { useCopy, useFramework } from "../framework";
import { iconFor } from "../components/icons";
import {
  EmptyState,
  PageHeader,
  StatusLegend,
  StatusMark,
} from "../components/ui";

const SearchIcon = iconFor("search");

const GROUP = SEARCH_GROUP;

/**
 * Content-class glyphs for the group headers, from the governed semantic
 * ledger. A group with no registered class meaning keeps its text label alone.
 */
export interface SearchGroupDefinition {
  id: string;
  label: string;
  order: number;
  glyph?: GlyphMeaning;
  showStatusLegend?: boolean;
}

/** Display order of the shared result groups. Audience-owned groups extend this list from their own entrypoint. */
function coreSearchGroups(
  copy: (key: string, fallback?: string) => string,
): readonly SearchGroupDefinition[] {
  return [
    {
      id: GROUP.pages,
      label: copy("page.search.group.pages", "Pages"),
      order: 0,
    },
    {
      id: GROUP.workblocks,
      label: copy(`page.search.group.${GROUP.workblocks}`),
      order: 1,
      glyph: "object.workblock",
    },
    {
      id: GROUP.businessModules,
      label: copy(`page.search.group.${GROUP.businessModules}`),
      order: 2,
      glyph: "class.business-module",
      showStatusLegend: true,
    },
    {
      id: GROUP.executionFlows,
      label: copy(`page.search.group.${GROUP.executionFlows}`),
      order: 3,
    },
    {
      id: GROUP.roles,
      label: copy(`page.search.group.${GROUP.roles}`),
      order: 4,
      glyph: "class.role",
    },
    {
      id: GROUP.workMoments,
      label: copy(`page.search.group.${GROUP.workMoments}`),
      order: 5,
      glyph: "object.workblock",
    },
    {
      id: GROUP.groundworksRecords,
      label: copy(`page.search.group.${GROUP.groundworksRecords}`),
      order: 6,
      glyph: "class.record",
    },
    {
      id: GROUP.connectedWork,
      label: copy(`page.search.group.${GROUP.connectedWork}`),
      order: 7,
      glyph: "capability.integration",
    },
    /* The 2026-08-19 search memo ruling: downloadable-asset bodies surface as
     * a governed Documents group placed directly after Connected Work. */
    {
      id: GROUP.documents,
      label: copy(`page.search.group.${GROUP.documents}`, "Documents"),
      order: 8,
    },
    {
      id: GROUP.officeCapabilities,
      label: copy(`page.search.group.${GROUP.officeCapabilities}`),
      order: 9,
      glyph: "capability.composer",
    },
    {
      id: GROUP.faqs,
      label: copy(`page.search.group.${GROUP.faqs}`),
      order: 10,
    },
    {
      id: GROUP.language,
      label: copy(`page.search.group.${GROUP.language}`),
      order: 11,
      glyph: "utility.glossary",
    },
    {
      id: GROUP.partnerCapabilityAreas,
      label: copy("partner-capability.search.areas"),
      order: 12,
    },
    {
      id: GROUP.partnerCapabilities,
      label: copy("partner-capability.search.capabilities"),
      order: 13,
    },
  ];
}

export type SearchEntry = SearchDocument;

export type SearchIndexExtension = (
  snapshot: FrameworkSnapshot,
  copy: (key: string, fallback?: string) => string,
) => SearchEntry[];

/** A provider may serve the generated per-audience artifact; the page falls back to building one from its snapshot. */
interface SearchIndexCapableProvider {
  getSearchIndex?: () => Promise<SearchIndexArtifact>;
}

/**
 * The provider-served artifact carries the downloadable-asset corpus the
 * in-browser build cannot see. A stale artifact is discarded rather than
 * served: its sourceContentHash must match the snapshot in view.
 */
function useProvidedSearchIndex(
  provider: unknown,
  snapshot: FrameworkSnapshot,
): SearchIndexArtifact | undefined {
  const [artifact, setArtifact] = useState<SearchIndexArtifact | undefined>(undefined);
  const getSearchIndex = (provider as SearchIndexCapableProvider | undefined)?.getSearchIndex;
  useEffect(() => {
    if (!getSearchIndex) return;
    let cancelled = false;
    getSearchIndex()
      .then((candidate) => {
        if (!cancelled) setArtifact(candidate);
      })
      .catch(() => {
        if (!cancelled) setArtifact(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [getSearchIndex]);
  return artifact && artifact.sourceContentHash === snapshot.generated.contentHash
    ? artifact
    : undefined;
}

const noAdditionalGroups: readonly SearchGroupDefinition[] = [];

export interface SearchPageProps {
  additionalGroups?: readonly SearchGroupDefinition[];
  extendIndex?: SearchIndexExtension;
}

export function SearchPage({
  additionalGroups = noAdditionalGroups,
  extendIndex,
}: SearchPageProps = {}) {
  const { snapshot, provider } = useFramework();
  const copy = useCopy();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const referenceOnly = searchParams.get("availability") === "reference";
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  const providedArtifact = useProvidedSearchIndex(provider, snapshot);
  const artifact = useMemo(
    () => providedArtifact ?? buildSearchIndex({ snapshot }),
    [providedArtifact, snapshot],
  );

  const extensionEntries = useMemo(() => {
    const resolveCopy = (key: string, fallback?: string) =>
      snapshot.copy[key] ?? fallback ?? key;
    return extendIndex?.(snapshot, resolveCopy) ?? [];
  }, [extendIndex, snapshot]);

  const groupDefinitions = useMemo(
    () =>
      [...coreSearchGroups(copy), ...additionalGroups].sort(
        (left, right) => left.order - right.order,
      ),
    [additionalGroups, copy],
  );

  const groups = useMemo(() => {
    const tokens = query.toLowerCase().split(/\s+/).filter(Boolean);
    if (!tokens.length && !referenceOnly) return [];
    const matches = [
      ...querySearchIndex(artifact, query, { referenceOnly }),
      ...extensionEntries
        .filter(
          (entry) =>
            !referenceOnly ||
            (entry.group === GROUP.businessModules &&
              entry.status === "reference"),
        )
        .map((entry) => ({
          entry,
          score: tokens.length ? scoreEntry(entry, tokens) : 1,
        }))
        .filter((match) => match.score > 0),
    ];
    return groupDefinitions
      .map((group) => ({
        ...group,
        entries: matches
          .filter((match) => match.entry.group === group.id)
          .sort(
            (a, b) =>
              b.score - a.score || a.entry.label.localeCompare(b.entry.label),
          )
          .map((match) => match.entry),
      }))
      .filter((group) => group.entries.length > 0);
  }, [artifact, extensionEntries, groupDefinitions, query, referenceOnly]);

  const total = groups.reduce(
    (count, group) => count + group.entries.length,
    0,
  );

  const clearAvailabilityFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("availability");
    return next.toString() ? `/search?${next.toString()}` : "/search";
  };

  const resultLinks = () =>
    Array.from(
      resultsRef.current?.querySelectorAll<HTMLAnchorElement>(
        "a[data-search-result]",
      ) ?? [],
    );

  const moveFocus = (delta: number) => {
    const links = resultLinks();
    if (!links.length) return;
    const current = links.indexOf(document.activeElement as HTMLAnchorElement);
    const next =
      current < 0
        ? delta > 0
          ? 0
          : links.length - 1
        : Math.min(Math.max(current + delta, 0), links.length - 1);
    links[next]?.focus();
  };

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      moveFocus(1);
    }
  };

  const handleResultsKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      moveFocus(event.key === "ArrowDown" ? 1 : -1);
    } else if (event.key === "Escape") {
      event.preventDefault();
      inputRef.current?.focus();
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const first = groups[0]?.entries[0];
    if (first) navigate(first.to);
  };

  return (
    <div className="apf-page apf-register-reference apf-search-page">
      <PageHeader
        eyebrow={copy("page.search.eyebrow")}
        title={copy("page.search.title")}
        description={copy("page.search.description")}
        meta={
          query || referenceOnly ? (
            <span>
              {total}{" "}
              {copy(
                total === 1
                  ? "page.search.result.singular"
                  : "page.search.result.plural",
              )}
            </span>
          ) : undefined
        }
      />
      <form
        className="apf-search-page__form"
        role="search"
        aria-label={copy("page.search.form-label")}
        onSubmit={handleSubmit}
      >
        <label className="apf-search-field apf-search-field--page">
          <SearchIcon size={16} />
          <span className="apf-visually-hidden">
            {copy("page.search.form-label")}
          </span>
          <input
            ref={inputRef}
            value={query}
            autoFocus
            autoComplete="off"
            placeholder={copy("page.search.placeholder")}
            onChange={(event) => {
              const value = event.target.value;
              const next = new URLSearchParams(searchParams);
              if (value) next.set("q", value);
              else next.delete("q");
              setSearchParams(next, { replace: true });
            }}
            onKeyDown={handleInputKeyDown}
          />
        </label>
      </form>
      <p role="status" className="apf-visually-hidden">
        {query || referenceOnly
          ? `${total} ${copy(total === 1 ? "page.search.result.singular" : "page.search.result.plural")}`
          : ""}
      </p>
      {referenceOnly ? (
        <p className="apf-search-hint">
          {copy("page.search.reference-filter")}{" "}
          <Link to={clearAvailabilityFilter()}>
            {copy("page.search.clear-filter")}
          </Link>
        </p>
      ) : !query ? (
        <p className="apf-search-hint">{copy("page.search.suggestion")}</p>
      ) : null}
      {query || referenceOnly ? (
        total ? (
          <div
            className="apf-search-results"
            ref={resultsRef}
            onKeyDown={handleResultsKeyDown}
          >
            {groups.map((group) => (
              <section
                className="apf-search-group"
                key={group.label}
                aria-label={group.label}
              >
                <h2>
                  {group.glyph ? (
                    <Glyph
                      meaning={group.glyph}
                      size={16}
                      className="apf-heading-glyph"
                    />
                  ) : null}
                  {group.label}
                </h2>
                {group.showStatusLegend ? <StatusLegend /> : null}
                <ul>
                  {group.entries.map((entry) => {
                    const Icon = entry.icon ? iconFor(entry.icon) : null;
                    return (
                      <li key={entry.key}>
                        <Link
                          className="apf-search-result"
                          to={entry.to}
                          data-search-result
                        >
                          {Icon ? <Icon size={20} /> : null}
                          <span className="apf-search-result__body">
                            <span className="apf-search-result__label">
                              <span>{entry.label}</span>
                              {entry.status ? (
                                <StatusMark value={entry.status} />
                              ) : null}
                            </span>
                            {entry.description ? (
                              <span className="apf-search-result__description">
                                {entry.description}
                              </span>
                            ) : null}
                            {entry.context ? (
                              <span className="apf-search-result__context">
                                {entry.context}
                              </span>
                            ) : null}
                          </span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>
        ) : (
          <EmptyState
            title={copy("page.search.empty.title")}
            description={copy(
              referenceOnly
                ? "page.search.empty.reference-description"
                : "page.search.empty.description",
            )}
          />
        )
      ) : null}
    </div>
  );
}
